import React, { useState } from "react";
import { 
    Paper, 
    Typography, 
    Stack, 
    TextField, 
    Divider, 
    Button,
    MenuItem,
    Box,
    Grid
} from "@mui/material";
import { useAuth } from "../hooks/useAuth";
import { saveFranchise } from "../hooks/apiHandler";

export const FranchiseForm = ({ onRequestClose }) => {
    const { user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);

    const {Logout} = useAuth();

    const [franchiseData, setFranchiseData] = useState({
        name: "",
        email: "",
        phone: "",
        status: "active",
        ownerUserID: user?.uid || user?.id || "", 
        headquartersAddress: {
            addressLine1: "",
            addressLine2: "",
            city: "",
            state: "",
            postCode: "",
            country: ""
        },
        brandSettings: {
            primaryLanguage: "en",
            defaultCurrency: "USD",
            industryVertical: 0,
            websiteThemeUrl: ""
        }
    });

    // Helper handler to update deep keys inside headquartersAddress state
    const handleAddressChange = (field, value) => {
        setFranchiseData(prev => ({
            ...prev,
            headquartersAddress: {
                ...prev.headquartersAddress,
                [field]: value
            }
        }));
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            // Generates the exact structured database payload mirroring your Mongoose model rules
            const databasePayload = {
                ...franchiseData,
                ownerUserID: user?.uid || user?.id || "unknown_owner",
                headquartersAddress: {
                    addressLine1: franchiseData.headquartersAddress.addressLine1.trim(),
                    addressLine2: franchiseData.headquartersAddress.addressLine2.trim(),
                    city: franchiseData.headquartersAddress.city.trim(),
                    state: franchiseData.headquartersAddress.state.trim(),
                    postCode: franchiseData.headquartersAddress.postCode.trim(),
                    country: franchiseData.headquartersAddress.country.trim()
                }
            };

            console.log("Submitting Cleaned Franchise Schema Payload:", databasePayload);
            
            // 💡 Production Hook:
            const response = await saveFranchise(databasePayload, Logout);
            console.log("Franchise creation response:", response);

            
            setTimeout(() => {
                setIsSaving(false);
                onRequestClose();
            }, 1000);
        } catch (error) {
            console.error("Database compilation rejection processing franchise schema:", error);
            setIsSaving(false);
        }
    };

    return (
        <Paper elevation={0} sx={{ p: 1, maxWidth: 500, width: "100%" }}>
            <Box sx={{ pb: 2 }}>
                <Typography variant="h5" fontWeight="bold" sx={{ mb: 0.5 }}>
                    Register Corporate Franchise
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Provide the baseline identity fields to launch a new brand cluster instance.
                </Typography>
            </Box>

            <form onSubmit={handleFormSubmit}>
                <Stack spacing={2.5}>
                    
                    {/* Brand Name Input Field */}
                    <TextField
                        label="Brand Name *"
                        fullWidth
                        size="small"
                        required
                        value={franchiseData.name}
                        onChange={(e) => setFranchiseData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g., MiiRA Burger Bar"
                    />

                    {/* Contact Email & Phone Grid Layout */}
                    <Grid container justifyContent="space-between">
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Corporate Email *"
                                type="email"
                                fullWidth
                                size="small"
                                required
                                value={franchiseData.email}
                                onChange={(e) => setFranchiseData(prev => ({ ...prev, email: e.target.value }))}
                                placeholder="billing@brand.com"
                            />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                            <TextField
                                label="Corporate Phone *"
                                fullWidth
                                size="small"
                                required
                                value={franchiseData.phone}
                                onChange={(e) => setFranchiseData(prev => ({ ...prev, phone: e.target.value }))}
                                placeholder="403-555-0199"
                            />
                        </Grid>
                    </Grid>

                    <Divider />
                    <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
                        Headquarters Address Details
                    </Typography>

                    {/* Address Line 1 & Line 2 stacked vertically */}
                    <TextField
                        label="Address Line 1 *"
                        fullWidth
                        size="small"
                        required
                        value={franchiseData.headquartersAddress.addressLine1}
                        onChange={(e) => handleAddressChange("addressLine1", e.target.value)}
                        placeholder="123 Business Trail NW"
                    />

                    <TextField
                        label="Address Line 2 (Suite, Unit, etc.)"
                        fullWidth
                        size="small"
                        value={franchiseData.headquartersAddress.addressLine2}
                        onChange={(e) => handleAddressChange("addressLine2", e.target.value)}
                        placeholder="Suite 404"
                    />

                    {/* City and Province/State Row */}
                    <Grid container justifyContent="space-between">
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="City *"
                                fullWidth
                                size="small"
                                required
                                value={franchiseData.headquartersAddress.city}
                                onChange={(e) => handleAddressChange("city", e.target.value)}
                                placeholder="Calgary"
                            />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                            <TextField
                                label="State / Province *"
                                fullWidth
                                size="small"
                                required
                                value={franchiseData.headquartersAddress.state}
                                onChange={(e) => handleAddressChange("state", e.target.value)}
                                placeholder="AB"
                            />
                        </Grid>
                    </Grid>

                    {/* Postal Code and Country Row */}
                    <Grid container justifyContent="space-between">
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Postal / ZIP Code *"
                                fullWidth
                                size="small"
                                required
                                value={franchiseData.headquartersAddress.postCode}
                                onChange={(e) => handleAddressChange("postCode", e.target.value)}
                                placeholder="T3M 3L7"
                            />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                            <TextField
                                label="Country *"
                                fullWidth
                                size="small"
                                required
                                value={franchiseData.headquartersAddress.country}
                                onChange={(e) => handleAddressChange("country", e.target.value)}
                                placeholder="Canada"
                            />
                        </Grid>
                    </Grid>

                    <Divider />

                    {/* Industry Vertical Category Selector Dropdown */}
                    <TextField
                        select
                        label="Industry Category *"
                        fullWidth
                        size="small"
                        value={franchiseData.brandSettings.industryVertical}
                        onChange={(e) => setFranchiseData(prev => ({
                            ...prev,
                            brandSettings: {
                                ...prev.brandSettings,
                                industryVertical: parseInt(e.target.value, 10)
                            }
                        }))}
                    >
                        <MenuItem value={0}>Fast Food / QSR</MenuItem>
                        <MenuItem value={1}>Fine Dining</MenuItem>
                        <MenuItem value={2}>Retail Logistics</MenuItem>
                        <MenuItem value={3}>Pizzeria / Cafe</MenuItem>
                        <MenuItem value={4}>Other Specialty</MenuItem>
                    </TextField>

                    <Divider sx={{ my: 1 }} />

                    {/* Operational Executions Action Container */}
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button variant="text" onClick={onRequestClose} disabled={isSaving}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="contained" disableElevation disabled={isSaving}>
                            {isSaving ? "Initializing..." : "Launch Instance"}
                        </Button>
                    </Stack>
                </Stack>
            </form>
        </Paper>
    );
};